// filepath: src/hooks/useLandlordDashboardData.js
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getDashboardAggregated,
  getDashboardSummary,
  getDashboardDetailed,
  getYoyGraphs,
  getYoyFacilityBreakdown,
  getYoyTiles,
} from "../api/LandlordDashboard";
import { getMTD, getYTD, parseAPIDate } from "../utils/date";

/* ---- helpers: read from either storage ---- */
const getAny = (key) =>
  localStorage.getItem(key) ?? sessionStorage.getItem(key) ?? null;

const readAnyJSON = (key, fallback = null) => {
  try {
    const raw = getAny(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export default function useLandlordDashboardData() {
  const [view, setView] = useState("MTD");
  const [tab, setTab] = useState("Revenue");
  const [revenueChart, setRevenueChart] = useState([]);
  const [reservationsChart, setReservationsChart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [yoyBars, setYoyBars] = useState({ revenue: [], reservations: [] });

  const [summary, setSummary] = useState({
    revenueCents: null,
    revenueChange: null,
    txCount: null,
    txChange: null,
  });

  const [facilityRowsMTD, setFacilityRowsMTD] = useState({
    revenue: [],
    reservations: [],
  });
  const [facilityRowsYTD, setFacilityRowsYTD] = useState({
    revenue: [],
    reservations: [],
  });

  // track selected facilities (we store these in localStorage on purpose)
  const [selectedIds, setSelectedIds] = useState(() => {
    try {
      const raw = localStorage.getItem("selected_facilities");
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.map(String) : [];
    } catch {
      return [];
    }
  });

  // bump this when storage "user" changes so we recompute facilities
  const [userVersion, setUserVersion] = useState(0);

  // compute complete list of facility IDs from the stored user (either storage)
  const allFacilities = useMemo(() => {
    const user = readAnyJSON("user", null);
    return (user?.facilities || []).map((f) => String(f.facility_id));
    // re-run when user changes in storage
  }, [userVersion]);

  const facility_ids = selectedIds.length ? selectedIds : allFacilities;

  // Use lag = 1 day (Chicago cutoff)
  const {
    date_from,
    date_to,
    date_to_lag,
    utcToday,
    first,
    yesterday,
    lagged,
  } = useMemo(() => getMTD(1), []);
  const { date_from: ytd_from, date_to: ytd_to } = useMemo(() => getYTD(1), []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    if (!facility_ids.length) {
      setLoading(false);
      return;
    }

    // axios instance adds Authorization automatically
    const bodyStd = { facility_ids, date_from, date_to, view_type: "mtd" };
    const bodyLag = {
      facility_ids,
      date_from,
      date_to: date_to_lag,
      view_type: "mtd",
    };

    try {
      // Aggregated chart series
      const agg = await getDashboardAggregated(bodyStd);
      const rows = Array.isArray(agg?.data?.data)
        ? agg.data.data
        : Array.isArray(agg?.data?.data?.data)
        ? agg.data.data.data
        : [];
      const sorted = [...rows].sort(
        (a, b) => parseAPIDate(a.date) - parseAPIDate(b.date)
      );

      // Revenue series
      const revBase = sorted.map((d) => ({
        x: parseAPIDate(d.date).getTime(),
        date: d.date,
        curr: d.remit?.mtd ?? null,
        prev: d.previous?.remit?.mtd ?? null,
      }));
      let lastCurrR = null,
        lastPrevR = null;
      const revClean = revBase.map((p) => {
        let { curr, prev } = p;
        if (curr === null && lastCurrR !== null) curr = lastCurrR;
        if (prev === null && lastPrevR !== null) prev = lastPrevR;
        if (curr !== null) lastCurrR = curr;
        if (prev !== null) lastPrevR = prev;
        return { ...p, curr, prev };
      });
      setRevenueChart(revClean);

      // Reservations series
      const resBase = sorted.map((d) => ({
        x: parseAPIDate(d.date).getTime(),
        date: d.date,
        curr: d.transactions?.mtd ?? null,
        prev: d.previous?.transactions?.mtd ?? null,
      }));
      let lastCurrT = null,
        lastPrevT = null;
      const resClean = resBase.map((p) => {
        let { curr, prev } = p;
        if (curr === null && lastCurrT !== null) curr = lastCurrT;
        if (prev === null && lastPrevT !== null) prev = lastPrevT;
        if (curr !== null) lastCurrT = curr;
        if (prev !== null) lastPrevT = prev;
        return { ...p, curr, prev };
      });
      setReservationsChart(resClean);

      // Summary (lagged)
      const sumRes = await getDashboardSummary(bodyLag);
      const payload = sumRes?.data;
      const mtd = payload?.data?.mtd ?? payload?.data?.data?.mtd ?? null;
      const toNum = (v) =>
        typeof v === "number" && !Number.isNaN(v) ? v : null;

      const revenueCents = toNum(mtd?.remit?.mtd);
      const revenueChange = toNum(mtd?.remit?.change);
      const txCount = toNum(mtd?.transactions?.mtd);
      const txChange = toNum(mtd?.transactions?.change);
      setSummary({ revenueCents, revenueChange, txCount, txChange });

      // Facility table (lagged)
      const det = await getDashboardDetailed(bodyLag);
      const raw = det?.data ?? {};
      const results =
        raw?.data?.results ?? raw?.data?.data?.results ?? raw?.results ?? [];

      if (!Array.isArray(results) || results.length === 0) {
        setFacilityRowsMTD({ revenue: [], reservations: [] });
      } else {
        const n = (v) => (v == null ? null : Number(v));
        const y = yesterday.getUTCDate();
        const getMonthDays = (d) =>
          new Date(
            Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)
          ).getUTCDate();
        const daysInMonth = getMonthDays(yesterday);

        const rowsRevenue = results.map((f) => {
          const name =
            f?.facility?.title ?? f?.facility?.name ?? f?.title ?? "—";
          const rating = n(f?.current_rating);
          const prevTotal = n(f?.previous?.remit?.total);
          const prevMTD = n(f?.previous?.remit?.mtd);
          const currMTD = n(f?.remit?.mtd);
          const currProjected =
            currMTD == null ? null : Math.round((currMTD / y) * daysInMonth);
          return { name, rating, prevTotal, prevMTD, currMTD, currProjected };
        });

        const rowsReservations = results.map((f) => {
          const name =
            f?.facility?.title ?? f?.facility?.name ?? f?.title ?? "—";
          const rating = n(f?.current_rating);
          const prevTotal = n(f?.previous?.transactions?.total);
          const prevMTD = n(f?.previous?.transactions?.mtd);
          const currMTD = n(f?.transactions?.mtd);
          const currProjected = n(f?.transactions?.projected);
          return { name, rating, prevTotal, prevMTD, currMTD, currProjected };
        });

        setFacilityRowsMTD({
          revenue: rowsRevenue,
          reservations: rowsReservations,
        });
      }
    } catch (err) {
      console.error(
        "MTD dashboard error:",
        err?.response?.status,
        err?.response?.data || err
      );
    } finally {
      setLoading(false);
    }
  }, [facility_ids, date_from, date_to, date_to_lag, yesterday]);

  const fetchYOY = useCallback(async () => {
    setLoading(true);
    try {
      if (!facility_ids.length) {
        setYoyBars({ revenue: [], reservations: [] });
        setFacilityRowsYTD({ revenue: [], reservations: [] });
        setLoading(false);
        return;
      }

      const bodyBase = {
        facility_ids,
        date_from: ytd_from,
        date_to: ytd_to,
        view_type: "ytd",
      };

      // YoY graphs
      const res = await getYoyGraphs(bodyBase);
      const root = res?.data;
      const container = root?.data?.data ?? root?.data ?? {};
      const monthsRev = container?.revenue ?? [];
      const monthsRes = container?.reservations ?? [];
      const currYear =
        Number(container?.date_range?.year) ||
        Number(root?.data?.date_range?.year) ||
        new Date().getUTCFullYear();
      const prevYear = currYear - 1;

      const labels = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const toNum = (v) => (v == null ? null : Number(v));
      const normalize = (arr) => {
        const mapByMonth = Object.fromEntries(
          (Array.isArray(arr) ? arr : []).map((m) => [String(m?.month), m])
        );
        return labels.map((lbl, i) => {
          const row = mapByMonth[lbl] || {};
          return {
            monthIdx: i,
            label: lbl,
            curr: toNum(row[currYear]),
            prev: toNum(row[prevYear]),
          };
        });
      };

      setYoyBars({
        revenue: normalize(monthsRev),
        reservations: normalize(monthsRes),
      });

      // Tiles
      try {
        const tilesRes = await getYoyTiles(bodyBase);
        const tRoot = tilesRes?.data;
        const tData = tRoot?.data?.data ?? tRoot?.data ?? {};

        const rev = tData?.revenue;
        const resv = tData?.reservations;

        let revenueCents = null,
          revenueChange = null,
          txCount = null,
          txChange = null;
        if (rev) {
          revenueCents = rev.value != null ? Number(rev.value) : null;
          revenueChange =
            rev.diff != null
              ? Number(rev.diff)
              : rev.change_pct != null
              ? Number(rev.change_pct)
              : null;
        }
        if (resv) {
          txCount = resv.value != null ? Number(resv.value) : null;
          txChange =
            resv.diff != null
              ? Number(resv.diff)
              : resv.change_pct != null
              ? Number(resv.change_pct)
              : null;
        }

        setSummary({ revenueCents, revenueChange, txCount, txChange });
      } catch (e) {
        console.error(
          "YTD tiles error:",
          e?.response?.status,
          e?.response?.data || e
        );
      }

      // Facility breakdown
      try {
        const fbRes = await getYoyFacilityBreakdown(bodyBase);
        const fbRoot = fbRes?.data;

        const primary =
          fbRoot?.data?.data?.data ??
          fbRoot?.data?.data ??
          fbRoot?.data?.results ??
          fbRoot?.data?.revenue ??
          fbRoot?.data ??
          [];

        const list = Array.isArray(primary) ? primary : [];
        const n = (v) => (v == null ? null : Number(v));

        const rowsRevenue = list.map((f) => {
          const name =
            f?.facility?.title ??
            f?.facility?.name ??
            f?.title ??
            f?.name ??
            "—";
          const rating = f?.current_rating ?? null;
          return {
            name,
            rating,
            prevYTD: n(f?.revenue?.previous_value),
            currYTD: n(f?.revenue?.value),
            prevTotal: n(f?.revenue?.previous_total),
          };
        });

        const rowsReservations = list.map((f) => {
          const name =
            f?.facility?.title ??
            f?.facility?.name ??
            f?.title ??
            f?.name ??
            "—";
          const rating = f?.current_rating ?? null;
          return {
            name,
            rating,
            prevYTD: n(f?.reservations?.previous_value),
            currYTD: n(f?.reservations?.value),
            prevTotal: n(f?.reservations?.previous_total),
          };
        });

        setFacilityRowsYTD({
          revenue: rowsRevenue,
          reservations: rowsReservations,
        });
      } catch (e) {
        console.error(
          "YTD facility breakdown error:",
          e?.response?.status,
          e?.response?.data || e
        );
        setFacilityRowsYTD({ revenue: [], reservations: [] });
      }
    } catch (err) {
      console.error(
        "YTD dashboard error:",
        err?.response?.status,
        err?.response?.data || err
      );
      setYoyBars({ revenue: [], reservations: [] });
      setFacilityRowsYTD({ revenue: [], reservations: [] });
    } finally {
      setLoading(false);
    }
  }, [facility_ids, ytd_from, ytd_to]);

  // run fetchers
  useEffect(() => {
    if (!facility_ids.length) return;
    if (view === "MTD") fetchAll();
    else fetchYOY();
  }, [facility_ids, view, fetchAll, fetchYOY]);

  // react to facility selection changes (localStorage)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "selected_facilities") {
        try {
          const raw = localStorage.getItem("selected_facilities");
          const arr = raw ? JSON.parse(raw) : [];
          setSelectedIds(Array.isArray(arr) ? arr.map(String) : []);
        } catch {
          setSelectedIds([]);
        }
      }
      if (e.key === "user") {
        setUserVersion((v) => v + 1);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const scopeLabel =
    facility_ids.length === allFacilities.length || facility_ids.length === 0
      ? "All facilities"
      : `${facility_ids.length} selected`;

  return {
    state: {
      view,
      tab,
      loading,
      revenueChart,
      reservationsChart,
      yoyBars,
      summary,
      facilityRowsMTD,
      facilityRowsYTD,
      allFacilities,
      facility_ids,
      date_from,
      date_to,
      date_to_lag,
      utcToday,
      first,
      yesterday,
      lagged,
      scopeLabel,
    },
    actions: { setView, setTab, setSelectedIds },
  };
}
